import { motion } from "motion/react";
import { ArrowRight, Shield, CheckCircle2, Eye } from "lucide-react";

export function TransparencySection() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          
          <h2 className="text-4xl md:text-5xl mb-6">
            Every Contribution,
            <br />
            <span className="text-primary">Every Impact - Tracked</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            As a foundation, we support and lead on-ground initiatives in education, skills, livelihoods, and sustainability across Himachal Pradesh. We work with partners who contribute time, expertise, and resources to strengthen these initiatives and expand their impact.
          </p>
        </motion.div>

        {/* Fund Flow Diagram */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="relative mb-16"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-8 h-full">
                <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center mb-6 text-2xl font-bold">
                  1
                </div>
                <h3 className="text-2xl mb-4">Take Action Together</h3>
                <p className="text-muted-foreground mb-4">
                  We initiate and run grassroots programs and invite individuals, educators, and organisations to contribute through teaching, mentoring, or enabling opportunities.
                </p>
                <div className="text-3xl font-bold text-primary">100%</div>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                <ArrowRight className="text-primary" size={32} />
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="bg-gradient-to-br from-secondary/5 to-secondary/10 rounded-2xl p-8 h-full">
                <div className="w-16 h-16 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center mb-6 text-2xl font-bold">
                  2
                </div>
                <h3 className="text-2xl mb-4">Collaborative Impact Delivery</h3>
                <p className="text-muted-foreground mb-4">
                  Contributions are channelled directly into our on-ground programs, implemented through trusted local partners and community networks we work with.
                </p>
                <div className="text-3xl font-bold text-secondary">100%</div>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                <ArrowRight className="text-primary" size={32} />
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-gradient-to-br from-accent/5 to-accent/10 rounded-2xl p-8 h-full">
              <div className="w-16 h-16 rounded-full bg-accent text-accent-foreground flex items-center justify-center mb-6 text-2xl font-bold">
                3
              </div>
              <h3 className="text-2xl mb-4">Transparent, Verified Outcomes</h3>
              <p className="text-muted-foreground mb-4">
                We document and share real-world impact through field updates, community feedback, and verified progress reports from each initiative.
              </p>
              <div className="text-3xl font-bold text-accent">100%</div>
            </div>
          </div>
        </motion.div>

        {/* Trust Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckCircle2 className="text-primary" size={28} />
            </div>
            <h4 className="text-xl mb-2">Verified Initiatives</h4>
            <p className="text-muted-foreground">
              All programs are designed, reviewed, and led by the foundation with clear impact goals and community alignment.
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <Eye className="text-primary" size={28} />
            </div>
            <h4 className="text-xl mb-2">Real-Time Engagement</h4>
            <p className="text-muted-foreground">
              Regular updates from the field through stories, visuals, and progress insights from ongoing initiatives.
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="text-primary" size={28} />
            </div>
            <h4 className="text-xl mb-2">Zero Commercial Layer</h4>
            <p className="text-muted-foreground">
              This is a foundation-led model. We do not operate as a fundraising marketplace and partners contribute directly to strengthen initiatives and expand on-ground reach.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
